import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PlayCircle, Trash2 } from 'lucide-react';
import AddIcon from '@mui/icons-material/Add';
import { Header } from '../../Components/Header/Header';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { EditButton, PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';

interface VendorCompany {
  id: number;
  sourcevend_company_id: number;
  name: string;
  active: boolean;
  default_aims_cid: number | null;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
}

interface ListResponse {
  data: VendorCompany[];
  meta?: { as_of?: string; total?: number };
}

const SEARCH_DEBOUNCE_MS = 300;

const headCellSx = {
  py: 1.25,
  px: 2,
  fontWeight: 700,
  fontSize: '0.6875rem',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: COLORS.lightGray,
  backgroundColor: '#f8f9fa',
  borderBottom: `1px solid ${COLORS.lightGray}35`,
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  py: 1.75,
  px: 2,
  borderBottom: `1px solid ${COLORS.lightGray}30`,
  verticalAlign: 'middle' as const,
};

export const VendingVendorCompaniesPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<VendorCompany[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [newCid, setNewCid] = useState('');

  const [removeTarget, setRemoveTarget] = useState<VendorCompany | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendingApiClient('/vendor-companies?limit=100');
      const json: ListResponse = await response.json();
      setRows(json.data);
      setAsOf(json.meta?.as_of ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendor companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetAddForm = () => {
    setNewId('');
    setNewName('');
    setNewCid('');
  };

  const handleCreate = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        sourcevend_company_id: Number(newId),
        name: newName.trim(),
      };
      if (newCid.trim()) body.default_aims_cid = Number(newCid);
      const response = await vendingApiClient('/vendor-companies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Create failed');
      }
      resetAddForm();
      setAddOpen(false);
      setMessage('Vendor company onboarded. Run Machines sync to pull its fleet.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = useCallback(async (row: VendorCompany) => {
    setError(null);
    try {
      await vendingApiClient(`/vendor-companies/${row.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !row.active }),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  }, [load]);

  const handleTest = useCallback(async (row: VendorCompany) => {
    setTestingId(row.id);
    setMessage(null);
    setError(null);
    try {
      const response = await vendingApiClient(`/vendor-companies/${row.id}/test`, {
        method: 'POST',
      });
      const json = await response.json();
      const data = json.data;
      if (data?.ok) {
        setMessage(
          `Test OK for ${data.name} (${data.sourcevend_company_id}): ${data.machine_count} machine(s) in ${data.duration_ms}ms`,
        );
      } else {
        setError(data?.error || 'Test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTestingId(null);
    }
  }, []);

  const handleRemove = async () => {
    if (!removeTarget) return;
    setRemovingId(removeTarget.id);
    setError(null);
    setMessage(null);
    try {
      const response = await vendingApiClient(`/vendor-companies/${removeTarget.id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Remove failed');
      }
      setMessage(`Removed ${removeTarget.name} (${removeTarget.sourcevend_company_id}) from sync registry.`);
      setRemoveTarget(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setRemovingId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!debouncedSearch) return rows;
    return rows.filter((r) => (
      r.name.toLowerCase().includes(debouncedSearch)
      || String(r.sourcevend_company_id).includes(debouncedSearch)
      || (r.notes || '').toLowerCase().includes(debouncedSearch)
      || String(r.default_aims_cid ?? '').includes(debouncedSearch)
    ));
  }, [rows, debouncedSearch]);

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.active).length,
    inactive: rows.filter((r) => !r.active).length,
  }), [rows]);

  const subtitle = useMemo(() => {
    const parts = [
      `${stats.total} compan${stats.total === 1 ? 'y' : 'ies'}`,
      `${stats.active} active`,
    ];
    if (stats.inactive) parts.push(`${stats.inactive} off`);
    if (asOf) parts.push(`As of ${formatUTC(asOf)} UTC`);
    return parts.join(' · ');
  }, [stats, asOf]);

  return (
    <PageContainer>
      <Header title="Vendor Companies" subtitle={subtitle} count={filtered.length}>
        {asOf && <FreshnessBadge asOf={asOf} />}
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
          sx={{ bgcolor: COLORS.teal, '&:hover': { bgcolor: COLORS.darkTeal }, textTransform: 'none' }}
        >
          Add company
        </Button>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: 2,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Search"
              placeholder="Name, companyId, notes…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 1 280px' } }}
            />
            {searchInput && (
              <Button variant="text" onClick={() => setSearchInput('')}>Clear</Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="text"
              onClick={() => navigate('/vending/integration-health')}
              sx={{ textTransform: 'none', color: COLORS.teal, fontWeight: 600 }}
            >
              Open Integration Health →
            </Button>
          </Box>

          {(error || message) && (
            <Box>
              {error && <Alert severity="error" sx={{ mb: message ? 1 : 0 }} onClose={() => setError(null)}>{error}</Alert>}
              {message && (
                <Alert severity="success" onClose={() => setMessage(null)}>
                  {message}
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && filtered.length === 0 && (
          <Box
            sx={{
              mx: { xs: 2, md: 3 },
              textAlign: 'center',
              py: 8,
              px: 2,
              borderRadius: 2,
              border: `1px dashed ${COLORS.lightGray}`,
              backgroundColor: '#fafbfc',
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
              No vendor companies
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.lightGray, mb: 2 }}>
              {rows.length === 0
                ? 'Onboard each SourceVend companyId so sync can pull that customer’s fleet.'
                : 'No companies match the current search.'}
            </Typography>
            {rows.length === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                sx={{ bgcolor: COLORS.teal, textTransform: 'none' }}
              >
                Add company
              </Button>
            )}
          </Box>
        )}

        {!loading && filtered.length > 0 && (
          <Box sx={{ flex: 1, minHeight: 0, px: { xs: 2, md: 3 }, pb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${COLORS.lightGray}40`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 880 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Company</TableCell>
                      <TableCell sx={headCellSx}>SourceVend ID</TableCell>
                      <TableCell sx={headCellSx}>Default AIMS CID</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Notes</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ '&:hover': { backgroundColor: '#fafbfc' } }}
                      >
                        <TableCell sx={bodyCellSx}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.darkGray }}>
                            {row.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: COLORS.lightGray }}>
                            Registry #{row.id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                              color: COLORS.teal,
                            }}
                          >
                            {row.sourcevend_company_id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Typography variant="body2" sx={{ color: row.default_aims_cid != null ? COLORS.darkGray : COLORS.lightGray }}>
                            {row.default_aims_cid ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={bodyCellSx}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch
                              size="small"
                              checked={row.active}
                              onChange={() => toggleActive(row)}
                            />
                            <Chip
                              size="small"
                              label={row.active ? 'Active' : 'Off'}
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: row.active ? `${COLORS.success}18` : `${COLORS.lightGray}20`,
                                color: row.active ? COLORS.success : COLORS.lightGray,
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, maxWidth: 260 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: row.notes ? COLORS.darkGray : COLORS.lightGray,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={row.notes || undefined}
                          >
                            {row.notes || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Test GetMachines against SourceVend">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={testingId === row.id}
                                  onClick={() => handleTest(row)}
                                  startIcon={
                                    testingId === row.id
                                      ? <CircularProgress size={14} sx={{ color: COLORS.white }} />
                                      : <PlayCircle size={16} />
                                  }
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    minWidth: 88,
                                    bgcolor: COLORS.teal,
                                    boxShadow: 'none',
                                    '&:hover': {
                                      bgcolor: COLORS.darkTeal,
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                    },
                                    '&.Mui-disabled': {
                                      bgcolor: `${COLORS.teal}60`,
                                      color: COLORS.white,
                                    },
                                  }}
                                >
                                  {testingId === row.id ? 'Testing…' : 'Test'}
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove from registry">
                              <span>
                                <EditButton
                                  disabled={removingId === row.id}
                                  onClick={() => setRemoveTarget(row)}
                                  title="Remove from registry"
                                  style={{ color: '#e05252' }}
                                >
                                  <Trash2 size={16} />
                                </EditButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        )}
      </Box>

      <Dialog open={addOpen} onClose={() => !saving && setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Onboard SourceVend company</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Enter the SourceVend companyId issued for this customer. Shared ApiKey comes from the integration env.
          </Typography>
          <TextField
            autoFocus
            size="small"
            label="SourceVend companyId"
            value={newId}
            onChange={(e) => setNewId(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Display name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            label="Default AIMS CID (optional)"
            value={newCid}
            onChange={(e) => setNewCid(e.target.value)}
            fullWidth
            helperText="Stored for convenience — machines are still mapped manually"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || !newId.trim() || !newName.trim()}
            onClick={handleCreate}
            sx={{ bgcolor: COLORS.teal, textTransform: 'none', fontWeight: 600 }}
          >
            {saving ? 'Saving…' : 'Add company'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removeTarget)} onClose={() => !removingId && setRemoveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove vendor company?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{removeTarget?.name}</strong> (companyId{' '}
            {removeTarget?.sourcevend_company_id}) from the sync registry? Existing synced
            machines stay in AIMS; this company will no longer be included in sync jobs.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemoveTarget(null)} disabled={Boolean(removingId)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={Boolean(removingId)}
            onClick={handleRemove}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {removingId ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};
